import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../utils/api';

function Login({ onLogin }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [azureEnabled, setAzureEnabled] = useState(false);
  const [showLocalLogin, setShowLocalLogin] = useState(false);
  const [azureLoading, setAzureLoading] = useState(false);

  useEffect(() => {
    // Check for OAuth errors in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get('error');
    if (oauthError) {
      setError(oauthError);
      // Clean up URL
      window.history.replaceState({}, document.title, '/login');
    }

    // Check if Azure auth is configured
    checkAzureConfig();

    // Check for saved credentials
    const savedCredentials = localStorage.getItem('savedCredentials');
    if (savedCredentials) {
      try {
        const parsed = JSON.parse(savedCredentials);
        setCredentials(parsed);
        setRememberMe(true);
        // Auto-login with saved credentials
        autoLogin(parsed);
      } catch (err) {
        console.error('Failed to parse saved credentials:', err);
        localStorage.removeItem('savedCredentials');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAzureConfig = async () => {
    try {
      const response = await authAPI.getAzureConfig();
      setAzureEnabled(response.data.enabled);
      // If Azure is not enabled, show local login by default
      if (!response.data.enabled) {
        setShowLocalLogin(true);
      }
    } catch (err) {
      console.error('Failed to check Azure config:', err);
      setShowLocalLogin(true);
    }
  };

  const autoLogin = async (creds) => {
    setIsLoading(true);
    try {
      await authAPI.login(creds);
      onLogin();
    } catch (err) {
      // If auto-login fails, just show the form with pre-filled credentials
      setError('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAzureLogin = async () => {
    setAzureLoading(true);
    setError('');
    try {
      const response = await authAPI.getAzureLoginUrl();
      window.location.href = response.data.authUrl;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate Microsoft login');
      setAzureLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authAPI.login(credentials);

      // Save credentials if Remember Me is checked
      if (rememberMe) {
        localStorage.setItem('savedCredentials', JSON.stringify(credentials));
      } else {
        localStorage.removeItem('savedCredentials');
      }

      onLogin();
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TCS Insight" className="h-20 w-auto mx-auto mb-4" />
          <p className="text-gray-600">MPAMOT Digital Solutions</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Azure SSO Button */}
        {azureEnabled && !showLocalLogin && (
          <div className="space-y-4">
            <button
              onClick={handleAzureLogin}
              disabled={azureLoading}
              className="btn-microsoft w-full"
            >
              {azureLoading ? (
                <span>Redirecting...</span>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-3"
                    viewBox="0 0 21 21"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#00a4ef" />
                    <rect x="1" y="11" width="9" height="9" fill="#7fba00" />
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>

            <button
              onClick={() => setShowLocalLogin(true)}
              className="btn btn-secondary w-full"
            >
              Sign in with local account
            </button>
          </div>
        )}

        {/* Local Login Form */}
        {showLocalLogin && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {azureEnabled && (
              <button
                type="button"
                onClick={() => {
                  setShowLocalLogin(false);
                  setError('');
                }}
                className="text-primary-600 hover:text-primary-700 text-sm flex items-center mb-4"
              >
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Microsoft sign-in
              </button>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username or email
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleChange}
                className="input"
                placeholder="Enter username or email"
                required
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                className="input"
                placeholder="Enter password"
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;
